// "Ask Marketing about this": the typed reference a chat message carries. The page that opens
// the chat puts it in `location.state.ask_about`; the composer shows it as a removable chip
// and sends it as `context_refs` (type + id only, never the item's text). A page that asks
// Marketing to do something (Settings' "Ask Marketing to update this") puts the message in
// `location.state.compose` instead; the composer starts with it, for the owner to finish.

import { useCallback } from 'react'
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'
import { useLocation, useNavigate } from 'react-router-dom'

export type EntityType = 'inbox_reply' | 'lead' | 'content_draft' | 'campaign' | 'shopify_product'

export interface AskAbout {
  entity_type: EntityType
  entity_id: string
  label: string
}

export interface ContextRef {
  entity_type: EntityType
  entity_id: string
}

export function toContextRefs(askAbout: AskAbout | undefined): ContextRef[] {
  return askAbout ? [{ entity_type: askAbout.entity_type, entity_id: askAbout.entity_id }] : []
}

/** The reference the current location carries, and a way to drop it. */
export function useAskAbout() {
  const location = useLocation()
  const navigate = useNavigate()
  const askAbout = (location.state as { ask_about?: AskAbout } | null)?.ask_about
  const clear = useCallback(() => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [navigate, location.pathname, location.search])
  return { askAbout, clear }
}

/** Opens the Marketing thread with `item` as the context chip. */
export function useOpenAskMarketing() {
  const navigate = useNavigate()
  return useCallback(
    (item: AskAbout) => navigate(CHAT_THREAD_PATH, { state: { ask_about: item } }),
    [navigate]
  )
}

/** Opens the Marketing thread with `text` typed into the composer, not yet sent. */
export function useOpenComposeMarketing() {
  const navigate = useNavigate()
  return useCallback(
    (text: string) => navigate(CHAT_THREAD_PATH, { state: { compose: text } }),
    [navigate]
  )
}

/** The message the current location asks the composer to start with, and a way to drop it. */
export function useComposeDraft() {
  const location = useLocation()
  const navigate = useNavigate()
  const draft = (location.state as { compose?: string } | null)?.compose
  const clear = useCallback(() => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [navigate, location.pathname, location.search])
  return { draft, clear }
}
